import { auth } from '../firebase';
import { getCourtLabel, formatTimeSlotSpan } from './bookingUtils';
import { formatMoney } from './currencyUtil';
import { getPaymentBreakdown } from './partialPaymentUtil';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// A `keepalive` request body is capped at 64KB by the browser, counted across
// every in-flight keepalive request in the tab. The club logo travels inside
// this payload as a base64 data URI (AdminConfig stores it that way, capped at
// 60KB on upload), so one large logo can exhaust that budget on its own and the
// request would be rejected outright. Payloads above this budget are re-sent
// without the logo instead.
const KEEPALIVE_BODY_BUDGET_BYTES = 56 * 1024;

/**
 * Formats a stored 'yyyy-MM-dd' booking date as "September 14, 2026" for the
 * guest email.
 *
 * Built from the string's own parts rather than `new Date(dateStr)`, which
 * parses a bare 'yyyy-MM-dd' as UTC midnight and would show the previous day
 * to anyone east of UTC — the club is in PH time (UTC+8).
 *
 * @param {string} dateStr - Date in 'yyyy-MM-dd' form.
 * @returns {string} Long-form date, or the original string if unparseable.
 */
const formatLongDate = (dateStr) => {
  const [year, month, day] = String(dateStr || '').split('-').map(Number);
  if (!year || !month || !day) return dateStr || '';
  return `${MONTH_NAMES[month - 1]} ${day}, ${year}`;
};

/**
 * Turns one date's `{ timeSlot: [courts] }` map inside out into a per-court
 * summary, so the email can list "Court 1: 7:00 - 10:00 PM" instead of
 * repeating the court on every hourly line.
 *
 * @param {object} timeSlots - e.g. { "7-8PM": [1, 2], "8-9PM": [1] }.
 * @param {object} config - Club config, for court display names.
 * @returns {{courts: Array<{label: string, time: string}>, timeLabel: string}}
 *   Per-court rows plus the overall span across every court that date.
 */
const summariseDate = (timeSlots, config) => {
  const slotsByCourt = {};
  Object.entries(timeSlots || {}).forEach(([timeSlot, courts]) => {
    // Courts arrive as an array from the booking flow but as an object's keys
    // once a booking has round-tripped through Firebase, which drops empty
    // arrays and reindexes the rest.
    const courtList = Array.isArray(courts) ? courts : Object.keys(courts || {});
    courtList.forEach(court => {
      if (!slotsByCourt[court]) slotsByCourt[court] = [];
      slotsByCourt[court].push(timeSlot);
    });
  });

  const courts = Object.keys(slotsByCourt)
    .sort((a, b) => Number(a) - Number(b))
    .map(court => ({
      label: getCourtLabel(config, court),
      time: formatTimeSlotSpan(slotsByCourt[court])
    }));

  // The headline "Date & Time" line spans every court booked that day.
  const allSlots = Object.keys(timeSlots || {});

  return { courts, timeLabel: formatTimeSlotSpan(allSlots) };
};

/**
 * Sends a booking confirmation/status email via the configured email backend
 * (Resend or Nodemailer, chosen through REACT_APP_EMAIL_USE_RESEND).
 *
 * @param {Object} bookingData - The booking record (date, timeSlots, email,
 *   userName, paymentAmount, status, confirmationId, trackingUrl,
 *   equipmentRental, ...).
 * @param {string} adminEmail - The admin's notification email address.
 * @param {boolean} withPaymentAttachment - Whether the backend should
 *   attach/reference the uploaded payment receipt in the email.
 * @param {object} [config] - the club config. REQUIRED in practice: it
 *   carries the on/off switch below as well as the court names, club name,
 *   logo and currency symbol, so omitting it silently sends nothing.
 * @param {object} [options] - Delivery options.
 * @param {boolean} [options.notifyAdmin=true] - Whether the admin copy is sent.
 *   Pass false for an action the admin performed themselves (confirming a
 *   booking), where mailing them a notification of their own click is noise.
 * @param {boolean} [options.notifyGuest=true] - Whether the guest copy is sent.
 *   Pass false when a guest submits a booking: they are only emailed once an
 *   admin confirms it.
 * @returns {Promise<void>} Resolves once the send attempt finishes. Errors
 *   are caught and logged rather than thrown, since a failed notification
 *   email should never block or roll back the booking flow itself — callers
 *   invoke this fire-and-forget (see AdminBooking.jsx's `.catch(() => {})`).
 */
export const sendConfirmationEmail = async (bookingData, adminEmail, withPaymentAttachment, config = null, options = {}) => {
  const { notifyAdmin = true, notifyGuest = true } = options;
  // Config -> System -> Send booking emails.
  //
  // `=== true`, not `!== false`: a club with no email backend configured — and
  // every config saved before this setting existed, where the key is simply
  // absent — must stay silent rather than fire requests at an endpoint that
  // isn't there. A caller that forgets to pass `config` therefore sends
  // nothing, which is the safe way round for something that mails guests.
  if (config?.emailSendingEnabled !== true) {
    return;
  }

  try {
    // Multi-date bookings (see App.jsx's createPendingBooking) carry a
    // `dates` array instead of just one `date`/`timeSlots` pair — fall back
    // to treating the single date/timeSlots fields as a one-entry list for
    // an ordinary single-date booking, so this always has at least one
    // group to describe.
    const dateGroups = Array.isArray(bookingData.dates) && bookingData.dates.length > 0
      ? bookingData.dates
      : [{ date: bookingData.date, timeSlots: bookingData.timeSlots }];

    // Structured per-date breakdown for the guest template, which lists each
    // court on its own line.
    const schedule = dateGroups.map(({ date, timeSlots }) => {
      const { courts, timeLabel } = summariseDate(timeSlots, config);
      return { dateLabel: formatLongDate(date), timeLabel, courts };
    });

    // Optional add-ons, already priced and labelled by the equipment flow
    // (see EquipmentRentalModal.jsx). The guest email shows what they took,
    // not what each line cost — the total below already covers the money.
    const addOns = (bookingData.equipmentRental?.items || []).map(item => ({
      label: item.label,
      qty: item.qty
    }));

    // Flat one-line summary, kept for the ADMIN templates, which still render
    // booking details as a single string:
    // "2026-09-14 @ 7-8PM (Court 1, Court 2) | 2026-09-15 @ ...".
    const bookingDetails = dateGroups.map(({ date, timeSlots }) => {
      const formattedTimeSlots = Object.entries(timeSlots || {})
        .map(([time, courts]) => {
          const courtList = Array.isArray(courts) ? courts : Object.keys(courts);
          return `${time} (${courtList.map(c => getCourtLabel(config, c)).join(', ')})`;
        })
        .join(', ');
      return `${date} @ ${formattedTimeSlots}`;
    }).join(' | ');

    const emailUrl = process.env.REACT_APP_EMAIL_USE_RESEND === 'Y'
      ? `${process.env.REACT_APP_EMAIL_ENDPOINT_BASE_URL}/resend`
      : `${process.env.REACT_APP_EMAIL_ENDPOINT_BASE_URL}/nodemailer`;

    // A partial payment — the guest's own "Partial payment" tick, or a
    // downpayment the admin recorded with Confirm (Partial) — is emailed as
    // amount paid, remaining balance and total. Worked out here, in numbers,
    // because the backend only ever sees formatted strings. Null (an
    // ordinary full payment) leaves the email showing its single amount.
    const breakdown = getPaymentBreakdown(bookingData, config);
    const isPartialPayment = breakdown !== null;

    const payload = {
      confirmationNumber: bookingData.confirmationId,
      // Blank when the guest skipped the optional field; the backend then
      // sends only the admin copy.
      guestEmail: bookingData.email || '',
      guestName: bookingData.userName || '',
      adminEmail: adminEmail || '',
      confirmedBy: auth.currentUser?.email || '',
      bookingDetails,
      // Branding for the guest email header. Absolute URL: an email client
      // has no app origin to resolve a relative path against.
      clubName: config?.courtName || '',
      logoUrl: config?.logoUrl || '',
      schedule,
      addOns,
      totalPayment: formatMoney(isPartialPayment ? breakdown.amountPaid : bookingData.paymentAmount, config),
      isPartialPayment,
      partialPercent: isPartialPayment ? breakdown.percent : null,
      fullTotalPayment: isPartialPayment ? formatMoney(breakdown.total, config) : null,
      remainingBalance: isPartialPayment ? formatMoney(breakdown.balance, config) : null,
      bookingStatus: bookingData.status,
      trackingUrl: bookingData.trackingUrl,
      isReceiptAttachment: withPaymentAttachment,
      notifyAdmin,
      notifyGuest
    };

    // Submitting the booking closes the checkout tab CHECKOUT_CLOSE_DELAY_MS
    // (150ms) later, which cancelled this request mid-flight and silently sent
    // nothing to either the guest or the admin. `keepalive` hands the request
    // to the browser to finish on its own after the page is gone, so the guest
    // never has to wait on the checkout screen for it.
    let body = JSON.stringify(payload);
    if (new Blob([body]).size > KEEPALIVE_BODY_BUDGET_BYTES) {
      // Dropping the logo degrades the email to the club name in type, which
      // is a far better outcome than the send failing altogether.
      body = JSON.stringify({ ...payload, logoUrl: '' });
    }

    const response = await fetch(emailUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
      keepalive: true,
    });

    const data = await response.json();
    if (!data.success) {
      // The backend returns `error` on a send failure and `message` only on an
      // unexpected 500, so log both — logging `message` alone printed
      // "Error sending email: undefined" for every real SMTP failure.
      console.error('Error sending email:', data.message || '', data.error || '');
    }
  } catch (error) {
    console.error('Network error:', error);
  }
};
